import React from 'react';

// Shared small pieces for the Compass Portal (v2 — agent-led portal).

// Map a source/stage status to a pill class + label.
const PILL = {
    'not-started': ['cp-pill--awaiting', 'Not started'],
    'in-progress': ['cp-pill--review', 'In progress'],
    blocked: ['cp-pill--flagged', 'Blocked'],
    complete: ['cp-pill--complete', 'Complete'],
    pending: ['cp-pill--pending', 'Pending'],
    awaiting: ['cp-pill--awaiting', 'Awaiting'],
    received: ['cp-pill--received', 'Received'],
    flagged: ['cp-pill--flagged', 'Flagged'],
    overdue: ['cp-pill--overdue', 'Overdue'],
    processing: ['cp-pill--review', 'Processing'],
    processed: ['cp-pill--complete', 'Processed'],
};

export function StatusPill({ status, label }) {
    const [cls, defLabel] = PILL[status] || ['cp-pill--awaiting', status || 'Unknown'];
    return <span className={`cp-pill ${cls}`}>{label || defLabel}</span>;
}

// Overall project status from its stages: complete if all complete, blocked if any
// blocked, in-progress if any started, else not-started.
export function overallStatus(stages = []) {
    if (!stages.length) return 'not-started';
    if (stages.every((s) => s.status === 'complete')) return 'complete';
    if (stages.some((s) => s.status === 'blocked')) return 'blocked';
    if (stages.some((s) => s.status === 'in-progress' || s.status === 'complete')) return 'in-progress';
    return 'not-started';
}

// Source collection status → pill status (collection tracker semantics).
export function sourceStatus(s) {
    if (s.processing_issue_status === 'open') return 'flagged';
    if (s.collected || s.file_url) return 'received';
    return 'pending';
}

export function isOverdue(dateStr) {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return !Number.isNaN(d.getTime()) && d < new Date(new Date().toDateString());
}

// RTK Query errors are objects, not Error instances — surface the backend's
// plain-language detail wherever it exists.
export function errMsg(e) {
    return (
        e?.data?.detail || e?.data?.error || e?.error ||
        (typeof e?.message === 'string' && e.message) ||
        (typeof e === 'string' && e) || 'Request failed'
    );
}

// Authenticated file download (the API needs the bearer header, so a plain <a href>
// can't be used) — fetch → blob → programmatic click. Matches the pattern the old
// DataProcessingPage used.
export async function downloadFile(token, projectId, sourceId, kind, filename) {
    const sub = kind === 'processed' ? 'download_processed' : 'download';
    const res = await fetch(
        `${process.env.REACT_APP_API_URL || ''}/compass_portal/projects/${projectId}/sources/${sourceId}/${sub}`,
        { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.click();
    URL.revokeObjectURL(url);
}
